import { Controller } from "@hotwired/stimulus"

// The message box: Enter sends, Shift+Enter breaks the line, files pasted
// or dropped go up as one message each, and the box empties after a send.
export default class extends Controller {
  static targets = ["editor", "clientId", "files"]

  connect() {
    this.onKeydown = (event) => this.keydown(event)
    this.element.addEventListener("keydown", this.onKeydown, true)
    // Attachments are on only so @mentions work; a file dropped or pasted
    // into the editor is rejected here and handled as its own message.
    this.onFileAccept = (event) => event.preventDefault()
    this.element.addEventListener("lexxy:file-accept", this.onFileAccept)
    this.newClientId()
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.onKeydown, true)
    this.element.removeEventListener("lexxy:file-accept", this.onFileAccept)
  }

  get editor() {
    return this.element.querySelector("lexxy-editor")
  }

  keydown(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing) return
    const editor = this.editor
    if (!editor || !editor.contains(event.target)) return
    if (editor.hasOpenPrompt) return
    event.preventDefault()
    event.stopPropagation()
    if (this.blank(editor.value)) return
    this.element.requestSubmit()
  }

  blank(html) {
    const text = (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim()
    return text === "" && !/<(img|action-text-attachment|video)/.test(html || "")
  }

  submitting() {
    this.element.querySelectorAll("button[type=submit]").forEach((b) => (b.disabled = true))
  }

  sent(event) {
    this.element.querySelectorAll("button[type=submit]").forEach((b) => (b.disabled = false))
    if (event.detail?.success === false) return
    const editor = this.editor
    if (editor) {
      editor.value = ""
      editor.focus?.()
    }
    this.newClientId()
  }

  edited(event) {
    if (event.detail?.success === false) return
    // The broadcast replaces the message with its fresh render.
  }

  newClientId() {
    if (this.hasClientIdTarget) this.clientIdTarget.value = crypto.randomUUID()
  }

  // --- files ---------------------------------------------------------

  pickFiles() {
    this.filesTarget.click()
  }

  filesPicked() {
    this.upload(Array.from(this.filesTarget.files))
    this.filesTarget.value = ""
  }

  paste(event) {
    const files = Array.from(event.clipboardData?.files || [])
    if (files.length === 0) return
    event.preventDefault()
    this.upload(files)
  }

  dragover(event) {
    if (event.dataTransfer?.types?.includes("Files")) event.preventDefault()
  }

  drop(event) {
    const files = Array.from(event.dataTransfer?.files || [])
    if (files.length === 0) return
    event.preventDefault()
    this.upload(files)
  }

  async upload(files) {
    for (const file of files) {
      const data = new FormData()
      data.append("chat_message[attachment]", file)
      data.append("chat_message[client_message_id]", crypto.randomUUID())
      await fetch(this.element.action, {
        method: "POST",
        body: data,
        headers: { "X-CSRF-Token": this.csrfToken, Accept: "application/json" }
      })
    }
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
